import React, { Fragment, useEffect, useMemo, useState } from 'react';
import SideNav from '../../Components/SideNav';
import Swal from 'sweetalert2';

import adminsHelper from '../../Helpers/Admin';
import { useAuth } from '../../Context/Context';
import { actualizarDatosSesion, leerDatosSesion } from '../../Helpers/sesion';
import { ADMIN_GENERAL, TIPOS_ADMIN, etiquetaTipo } from '../../Helpers/tiposAdmin';

import Loader from '../../Components/Loader';

import './EditarAdministrador.css';

const estiloAyuda = { color: "#757575", fontSize: "13px", margin: "-10px 0 10px" };

const extraerDatos = admin => ({ tipoMostrar: admin?.tipoMostrar ?? '', correo: admin?.correo ?? '', tipo: admin?.tipo ?? '' });

const EditarAdministrador = () => {
    const { recargar, setUser } = useAuth();

    const id = window.location.pathname.split("/")[2];
    // Se lee directo de la sesión: el Context se llena hasta que monta el SideNav
    const sesion = useMemo(() => leerDatosSesion(), []);
    const esPropia = sesion?.id === id;
    const esGeneral = sesion?.tipo === ADMIN_GENERAL;
    // Igual que la API: cada quien edita su cuenta, un general edita a todos y nadie cambia su propio tipo
    const puedeEditar = esPropia || esGeneral;
    const puedeCambiarTipo = esGeneral && !esPropia;

    const [guardado, setGuardado] = useState(null);
    const [datos, setDatos] = useState(extraerDatos(null));
    const [password, setPassword] = useState('');
    const [confirmacion, setConfirmacion] = useState('');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        obtenerAdmin();
    }, []);

    const obtenerAdmin = async () => {
        let res = await adminsHelper.obtenerUnAdmin(id);
        if (res.success) {
            const extraidos = extraerDatos(res.data);
            setGuardado(extraidos);
            setDatos(extraidos);
        } else {
            Swal.fire('Error!', 'No se logro obtener el administrador, codigo: ' + res.code, 'error');
        }
    }

    const cambiar = cambios => setDatos(datos => ({ ...datos, ...cambios }));

    const hayCambios = Boolean(guardado) && (JSON.stringify(datos) !== JSON.stringify(guardado) || Boolean(password) || Boolean(confirmacion));

    const descartar = () => {
        setDatos(guardado);
        setPassword('');
        setConfirmacion('');
    }

    const errores = () => {
        const faltantes = [[datos.tipoMostrar.trim(), 'Nombre'], [datos.correo.trim(), 'Correo']].filter(([valor]) => !valor).map(([, nombre]) => nombre);
        if (faltantes.length) return 'Faltan datos por llenar: ' + faltantes.join(', ');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(datos.correo.trim())) return 'El correo no es válido';
        if (password || confirmacion) {
            if (password.length < 8) return 'La contraseña debe tener al menos 8 caracteres';
            if (password !== confirmacion) return 'Las contraseñas no coinciden';
        }
        return null;
    }

    const actualizar = async e => {
        e.preventDefault();
        const error = errores();
        if (error) return Swal.fire('Error!', error, 'error');
        setGuardando(true);
        const res = await adminsHelper.editarAdmin({
            tipoMostrar: datos.tipoMostrar.trim(),
            correo: datos.correo.trim(),
            tipo: puedeCambiarTipo ? datos.tipo : undefined,
            password
        }, id);
        setGuardando(false);
        if (!res?.success) return;
        // El header lee el nombre de la sesión: se actualiza para no esperar a volver a iniciar sesión
        if (esPropia) setUser(actualizarDatosSesion({ nombre: datos.tipoMostrar.trim(), puesto: datos.tipoMostrar.trim() }));
        recargar();
    }

    const iniciales = (guardado?.tipoMostrar ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(palabra => palabra[0].toUpperCase()).join('');

    return (
        <Fragment>
            <SideNav />
            {guardando && (
                <div style={{width:"100%", height:"100vh", position:"fixed", top:"0", background:"#00000059", zIndex:"9999"}}>
                    <Loader />
                </div>
            )}
            <div className='perfil_admin'>
                {esGeneral && (
                    <a href='/administradores' className='perfil_admin_volver'>
                        <i className='material-icons'>arrow_back</i>
                        Administradores
                    </a>
                )}
                <h1 className='perfil_admin_titulo'>{esPropia ? 'Mi perfil' : 'Editar administrador'}</h1>

                {!guardado ? (
                    <div className='perfil_admin_tarjeta'>
                        <Loader />
                    </div>
                ) : (
                    <Fragment>
                        <div className='perfil_admin_tarjeta perfil_admin_resumen'>
                            <span className='perfil_admin_avatar'>{iniciales}</span>
                            <div>
                                <p className='perfil_admin_nombre'>{guardado.tipoMostrar}</p>
                                <p className='perfil_admin_dato'>{guardado.correo}</p>
                                <span className='perfil_admin_chip'>{etiquetaTipo(guardado.tipo)}</span>
                            </div>
                        </div>

                        {!puedeEditar && (
                            <p className='perfil_admin_aviso'>Solo un administrador general puede editar a otros administradores.</p>
                        )}

                        <form onSubmit={actualizar}>
                            <fieldset disabled={!puedeEditar} className='perfil_admin_campos'>
                                <section className='perfil_admin_tarjeta'>
                                    <h2 className='perfil_admin_seccion'>Datos de la cuenta</h2>
                                    <div className="input-field">
                                        <input onChange={e => cambiar({ tipoMostrar: e.target.value })} value={datos.tipoMostrar} id="tipoMostrar" type="text" maxLength={100} />
                                        <label className='active' htmlFor="tipoMostrar">Nombre</label>
                                    </div>
                                    <p style={estiloAyuda}>Es el nombre que aparece en el header del panel.</p>
                                    <div className="input-field">
                                        <input onChange={e => cambiar({ correo: e.target.value })} value={datos.correo} id="correo" type="email" />
                                        <label className='active' htmlFor="correo">Correo</label>
                                    </div>
                                    <p style={estiloAyuda}>Con este correo se inicia sesión.</p>
                                </section>

                                <section className='perfil_admin_tarjeta'>
                                    <h2 className='perfil_admin_seccion'>Tipo de usuario</h2>
                                    {puedeCambiarTipo ? (
                                        <Fragment>
                                            <select
                                                id='tipo'
                                                className='browser-default'
                                                value={datos.tipo}
                                                onChange={e => cambiar({ tipo: e.target.value })}
                                            >
                                                {TIPOS_ADMIN.map(({ valor, etiqueta }) => (
                                                    <option key={valor} value={valor}>{etiqueta}</option>
                                                ))}
                                            </select>
                                            <p style={{ ...estiloAyuda, margin: "8px 0 0" }}>Define qué secciones ve en el menú.</p>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <p className='perfil_admin_valor'>{etiquetaTipo(datos.tipo)}</p>
                                            {esPropia && (
                                                <p style={{ ...estiloAyuda, margin: "0" }}>No puedes cambiar tu propio tipo; pídeselo a un administrador general.</p>
                                            )}
                                        </Fragment>
                                    )}
                                </section>

                                <section className='perfil_admin_tarjeta'>
                                    <h2 className='perfil_admin_seccion'>Contraseña</h2>
                                    <div className="input-field">
                                        <input onChange={e => setPassword(e.target.value)} value={password} id="password" type="password" autoComplete="new-password" />
                                        <label className='active' htmlFor="password">Nueva contraseña</label>
                                    </div>
                                    <div className="input-field">
                                        <input onChange={e => setConfirmacion(e.target.value)} value={confirmacion} id="confirmacion" type="password" autoComplete="new-password" />
                                        <label className='active' htmlFor="confirmacion">Confirmar contraseña</label>
                                    </div>
                                    <p style={estiloAyuda}>Déjala vacía para conservar la actual. Mínimo 8 caracteres.</p>
                                </section>

                                {puedeEditar && (
                                    <div className='perfil_admin_acciones'>
                                        <button type='button' className='boton_admin boton_admin_secundario' onClick={descartar} disabled={!hayCambios}>
                                            Descartar
                                        </button>
                                        <button type='submit' className='boton_admin' disabled={!hayCambios}>
                                            Guardar cambios
                                        </button>
                                    </div>
                                )}
                            </fieldset>
                        </form>
                    </Fragment>
                )}
            </div>
        </Fragment>
    );
};

export default EditarAdministrador;
